/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../assets/css/aunjai-step2.css";
import { useLoanStore } from "../../stores/useLoanStore";
import Header from "../Header/stepHeader";
import SelectInput from "../../components/form/SelectInput";
import Column from "../../components/ui/Stack/Column";
import { useFetchMouCompanies } from "../../features/loans/hooks/useFetchMouCompanies";
import { useFetchLoans } from "../../hooks/fetch-data/useFetchLoans";
import LoadingPage from "../../components/LoadingPage/LoadingPage";
import { useTranslation } from "react-i18next";
import { CONTACT } from "../../constants/contact";

const FillFormStep2 = () => {
  const { t, i18n } = useTranslation('form2');

  const navigate = useNavigate();
  const [vehicleType, setVehicleType] = useState("sedan");
  const [loanAmount, setLoanAmount] = useState(0);
  const [installmentPeriod, setInstallmentPeriod] = useState(0);
  const [monthlyPayment, setMonthlyPayment] = useState(0);
  const [creditPurpose, setCreditPurpose] = useState("การซื้อสินค้า");

  const { data: mouCompanies, isFetching: isFetchingMouCompanies } =
    useFetchMouCompanies();

  const { data: products, isFetching: isFetchingProducts } = useFetchLoans();

  const { current_company: currentCompany } = useLoanStore(
    (state) => state.firstFillFormData
  );

  const mouCompanyInfo = mouCompanies?.find(
    (company) => company.id == currentCompany
  );

  const { is_mou: isMOU } = useLoanStore((state) => state.incomeFormProfile);

  const storeSelectedLoan = useLoanStore((state) => state.selectedLoan);
  const setSelectedLoan = useLoanStore((state) => state.setSelectedLoan);

  // Fallback: if store has no selectedLoan, use first product from API
  const selectedLoan = storeSelectedLoan || (products?.[0] || null);

  // Sync back to store so subsequent pages have it
  useEffect(() => {
    if (!storeSelectedLoan && selectedLoan) {
      setSelectedLoan(selectedLoan);
    }
  }, [storeSelectedLoan, selectedLoan, setSelectedLoan]);

  const setSecondFillFormData = useLoanStore(
    (state) => state.setSecondFillFormData
  );

  // Derived values (safe even if selectedLoan/mouCompanyInfo is null)
  const loanAmountLimit = isMOU
    ? (mouCompanyInfo?.mou_amount || 0)
    : (selectedLoan?.loan_amount || 0);

  const loanRate = isMOU
    ? (mouCompanyInfo?.mou_rate || 0)
    : (selectedLoan?.rate || 0);

  const maxInstallmentPeriod = isMOU
    ? (mouCompanyInfo?.loan_term_by_month || 12)
    : (selectedLoan?.loan_term_by_month || 12);

  const depositAmount = selectedLoan?.deposit_amount || 4999;

  // Vehicle type options with prices derived from product loan_amount
  const vehicleOptions = [
    { value: "sedan", label: t("vehicleSedan"), price: loanAmountLimit },
    { value: "van", label: t("vehicleVan"), price: loanAmountLimit + 3000 },
    { value: "suv", label: t("vehicleSuv"), price: loanAmountLimit + 2000 },
  ];

  // Set installmentPeriod from product's loan_term_by_month (fixed, not editable)
  useEffect(() => {
    if (maxInstallmentPeriod) {
      setInstallmentPeriod(maxInstallmentPeriod);
    }
  }, [maxInstallmentPeriod]);

  // Set loanAmount when vehicleType changes or on initial load
  useEffect(() => {
    const selected = vehicleOptions.find((v) => v.value === vehicleType);
    if (selected) {
      setLoanAmount(selected.price);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleType, loanAmountLimit]);

  // Calculate monthly payment whenever values change
  useEffect(() => {
    const principal = loanAmount - depositAmount;
    if (principal <= 0 || installmentPeriod <= 0) {
      setMonthlyPayment(0);
      return;
    }

    const rate = loanRate / 100;
    let monthly;
    if (rate === 0) {
      monthly = principal / installmentPeriod;
    } else {
      const totalPayment =
        principal + principal * rate * (installmentPeriod / 12);
      monthly = totalPayment / installmentPeriod;
    }

    setMonthlyPayment(monthly.toFixed(2));
  }, [loanAmount, installmentPeriod, depositAmount, loanRate]);

  const handleNextStep = () => {
    setSecondFillFormData({
      loan_amount: loanAmount,
      installment_period: installmentPeriod,
      monthly_payment: monthlyPayment,
      credit_purpose: creditPurpose,
      vehicle_type: vehicleType,
    });
    navigate("/loan-process/form-step4");
  };

  const handlePeriodChange = (e) =>
    setInstallmentPeriod(Number(e.target.value));

  const handleVehicleTypeChange = (value) => {
    setVehicleType(value);
  };

  // Early returns AFTER all hooks
  if (isFetchingMouCompanies || isFetchingProducts) {
    return <LoadingPage message={t('loading')} />;
  }

  if (!isMOU && !selectedLoan) {
    return (
      <Column>
        <p>{t('notFound')}</p>
      </Column>
    );
  }

  if (isMOU && !mouCompanyInfo) {
    return (
      <Column>
        <p>{t('notFound')}</p>
      </Column>
    );
  }

  return (
    <div id="pageFillForm2">
      <Header
        title={t('header')}
        showIcon={false}
        onBack={() => navigate(-1)}
        step={3}
      />
      <div className="wrapContent">
        <div id="pFillForm2">
          <div className="bFillForm">
            <div className="bContent">
              <div className="bTop">
                <div className="textTop">
                  <h3>{t('title')}</h3>
                </div>
              </div>

              {/* Vehicle Type — dropdown style */}
              <div style={{ padding: "0 24px", marginTop: 16 }}>
                <div style={{
                  border: "1px solid var(--color-border, #E6E4E4)",
                  borderRadius: 10,
                  padding: "14px 16px",
                  marginBottom: 12,
                }}>
                  <p style={{ fontSize: 12, color: "var(--color-text-secondary, #78706E)", marginBottom: 6 }}>{t('vehicleTypeLabel')}</p>
                  <select
                    value={vehicleType}
                    onChange={(e) => handleVehicleTypeChange(e.target.value)}
                    style={{
                      width: "100%",
                      border: "none",
                      fontSize: 16,
                      fontWeight: 600,
                      color: "var(--color-text-primary, #322E2D)",
                      background: "transparent",
                      outline: "none",
                      padding: 0,
                    }}
                  >
                    {vehicleOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label} — {Number(option.price).toLocaleString()} {t('baht')}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Contract Term — read-only field style */}
                <div style={{
                  border: "1px solid var(--color-border, #E6E4E4)",
                  borderRadius: 10,
                  padding: "14px 16px",
                  marginBottom: 12,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}>
                  <div>
                    <p style={{ fontSize: 12, color: "var(--color-text-secondary, #78706E)", marginBottom: 4 }}>{t('paymentTerm')}</p>
                    <p style={{ fontSize: 16, fontWeight: 600, color: "var(--color-text-primary, #322E2D)" }}>{installmentPeriod} {t('term')}</p>
                  </div>
                  <p style={{ fontSize: 20, fontWeight: 700, color: "var(--color-text-primary, #322E2D)" }}>
                    ฿{Number(loanAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              </div>

              {/* Summary Card — orange border */}
              <div style={{
                margin: "20px 24px",
                border: "2px solid var(--color-primary, #FF6500)",
                borderRadius: 14,
                padding: "24px 20px",
                background: "#fff",
              }}>
                {/* Monthly Payment — large display */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 8 }}>
                  <div>
                    <p style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.3, color: "var(--color-primary, #FF6500)" }}>{t('monthlyPaid')}</p>
                    <p style={{ fontSize: 12, color: "var(--color-text-secondary, #78706E)" }}>โดยประมาณ</p>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <span style={{ fontSize: 32, fontWeight: 700, color: "var(--color-primary, #FF6500)" }}>
                      ฿{Number(monthlyPayment).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>

                {/* First payment */}
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  paddingTop: 12,
                  borderTop: "1px solid var(--color-border, #E6E4E4)",
                  marginTop: 8,
                }}>
                  <p style={{ fontSize: 14, color: "var(--color-text-primary, #322E2D)" }}>{t('depositLabel')}</p>
                  <p style={{ fontSize: 16, fontWeight: 600, color: "var(--color-text-primary, #322E2D)" }}>
                    ฿{Number(depositAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>

                {/* Contract term */}
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: 8,
                }}>
                  <p style={{ fontSize: 14, color: "var(--color-text-primary, #322E2D)" }}>{t('paymentTerm')}</p>
                  <p style={{ fontSize: 16, fontWeight: 600, color: "var(--color-text-primary, #322E2D)" }}>{installmentPeriod} {t('term')}</p>
                </div>
              </div>

              {/* Terms */}
              <div style={{ padding: "0 24px", marginTop: 8, marginBottom: 16 }}>
                <p style={{ fontSize: 11, color: "var(--color-text-secondary, #78706E)", lineHeight: 1.6 }}>
                  * ราคาและโปรโมชั่นอาจแตกต่างกันไปในแต่ละสาขา กรุณาตรวจสอบกับเจ้าหน้าที่อีกครั้ง
                </p>
                <p style={{ fontSize: 11, color: "var(--color-text-secondary, #78706E)", lineHeight: 1.6 }}>
                  * ยอดชำระต่อเดือนเป็นการประมาณการเบื้องต้น ยอดจริงอาจเปลี่ยนแปลงตามเงื่อนไขของบริการ
                </p>
                <p style={{ fontSize: 11, color: "var(--color-text-secondary, #78706E)", lineHeight: 1.6 }}>
                  * สอบถามรายละเอียดเพิ่มเติม โทร {CONTACT.PHONE_DISPLAY}
                </p>
              </div>
            </div>
            <footer className="footer">
              <div className="bBtn bActionButton">
                <button type="button" onClick={handleNextStep}>
                  {t('confirm')}
                </button>
              </div>
            </footer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FillFormStep2;
